import Highcharts from 'highcharts';

import { getChartCapabilities, isHourOfDay } from './chartOptions';
import { ForecastPoint, toForecastPoints } from './forecastPoints';

export { toForecastPoints };
export type { ForecastPoint };

export const DEFAULT_FORECAST_PERIODS = 3;
// As aggregateCompoundMetric allows
export const MAX_FORECAST_PERIODS = 24;

// Charts that plot time along the x-axis: Line (not split by a dimension),
// Bar and Combo when they're drawn over time (no dimension), unless bucketed
// by hour of the day. See buildChartDefinition.
export const canForecast = (
  content: Pick<
    Studio.ChartContent,
    'visType' | 'metricIds' | 'dimensions' | 'chart'
  >,
): boolean =>
  ((content.visType === 'line' && !getChartCapabilities(content).isSplitLine) ||
    ((content.visType === 'bar' || content.visType === 'combo') &&
      (content.dimensions ?? []).length === 0)) &&
  !isHourOfDay(content);

// Loose views of the Highcharts options the V5 gadgets build
type SourceSeries = Highcharts.SeriesOptions & {
  data?: unknown[];
  color?: string;
  yAxis?: number | string;
  linkedTo?: string;
  dashStyle?: string;
  metricId?: string;
  seriesKey?: string;
};
type Axis = Highcharts.XAxisOptions;

const FORECAST_SUFFIX = ' (forecast)';

// Styled by StudioChart's chart body: the gadgets' global chart styles draw
// every line 3px wide, the band's edges included, so the forecast's widths
// are set there rather than as series options
export const FORECAST_CLASS_NAMES = {
  band: 'studio-forecast-band',
  line: 'studio-forecast-line',
  period: 'studio-forecast-period',
  start: 'studio-forecast-start',
};
const COMPARISON_SUFFIX = ' - Last Period';

// Metric series only: not derived ones (average, trend), comparisons, or
// forecasts already added
const isSourceSeries = (series: SourceSeries): boolean =>
  !series.linkedTo &&
  series.type !== 'arearange' &&
  !(series.name ?? '').endsWith(COMPARISON_SUFFIX) &&
  // A comparison is dashed, whatever it's named
  series.dashStyle !== 'Dash' &&
  !(series.name ?? '').endsWith(FORECAST_SUFFIX) &&
  Array.isArray(series.data) &&
  series.data.length >= 2 &&
  // Only the chart's own query is forecast, not a series with query
  // overrides (keyed by its own id, see seriesDataKey)
  (!series.seriesKey || series.seriesKey === series.metricId);

const toY = (point: unknown): number => {
  if (typeof point === 'number') return point;
  if (Array.isArray(point)) return Number(point[1]);
  if (point && typeof point === 'object' && 'y' in point) {
    return Number((point as { y: unknown }).y);
  }
  return NaN;
};

// The dashed average line and (unless hidden) shaded band for one series,
// starting at its last actual point so they join the line
const forecastSeries = ({
  source,
  id,
  color,
  xs,
  last,
  forecast,
  showRange,
}: {
  source: SourceSeries;
  id: string;
  color: string;
  xs: number[];
  // The last actual point with a value
  last: { x: number; y: number };
  forecast: ForecastPoint[];
  showRange: boolean;
}): Highcharts.SeriesOptionsType[] => {
  const shared = {
    linkedTo: id,
    color,
    yAxis: source.yAxis,
    metricId: source.metricId,
    marker: { enabled: false },
    isForecast: true,
    // Not faded while another series is hovered, e.g. the band while its
    // line's tooltip shows the range
    states: { inactive: { opacity: 1 } },
  };
  // Ids of their own: the chart updates in place, matching series by id and
  // otherwise by position, so without them the line would take over the
  // band's series (its settings and all) when the band is hidden
  const band: Highcharts.SeriesArearangeOptions = {
    ...shared,
    id: `${id}-forecast-range`,
    type: 'arearange',
    name: `${source.name ?? ''} range${FORECAST_SUFFIX}`,
    data: [
      [last.x, last.y, last.y],
      ...forecast.map((p, i) => [xs[i], p.low, p.high]),
    ],
    className: FORECAST_CLASS_NAMES.band,
    fillOpacity: 0.3,
    lineWidth: 0,
    zIndex: 0,
    // Its points have no single value for the gadgets' tooltips to show
    enableMouseTracking: false,
  };
  return [
    ...(showRange ? [band] : []),
    {
      ...shared,
      id: `${id}-forecast`,
      type: 'line',
      name: `${source.name ?? ''}${FORECAST_SUFFIX}`,
      // Each point keeps its range for the tooltip (see withRanges)
      data: [
        { x: last.x, y: last.y },
        ...forecast.map((p, i) => ({
          x: xs[i],
          y: p.avg,
          ...(showRange ? { low: p.low, high: p.high } : {}),
        })),
      ],
      className: FORECAST_CLASS_NAMES.line,
      dashStyle: 'Dash',
      zIndex: 1,
    } as Highcharts.SeriesLineOptions,
  ];
};

type FormatValue = (args: { metricId: string; value: number }) => string;

// A tooltip's point, as far as its range needs
interface TooltipPoint {
  point: { options: object };
  series: { userOptions: object };
}

// A tooltip's text with each forecast point's 95% range on a line after it.
// An HTML tooltip starts the line with a block; an SVG one (text only) with
// a break, since it drops the text's own trailing break.
export const withRanges = ({
  text,
  points,
  formatValue,
  isHtml,
}: {
  text: string;
  points: TooltipPoint[];
  formatValue: FormatValue;
  isHtml: boolean;
}): string => {
  const ranges = points.flatMap((p) => {
    const { low, high } = p.point.options as { low?: number; high?: number };
    const { metricId, isForecast } = p.series.userOptions as {
      metricId?: string;
      isForecast?: boolean;
    };
    if (
      !isForecast ||
      !metricId ||
      typeof low !== 'number' ||
      typeof high !== 'number'
    ) {
      return [];
    }
    const range = `${formatValue({ metricId, value: low })} – ${formatValue({ metricId, value: high })}`;
    // The space inside the label: SVG tooltips drop it between tags
    const line = `<span style="color: var(--color-text-tertiary);">95% range: </span><b>${range}</b>`;
    return [isHtml ? `<div>${line}</div>` : `<br/>${line}`];
  });
  return ranges.length > 0 ? `${text}${ranges.join('')}` : text;
};

// The chart's own tooltip, with the forecast points' ranges added (see
// withRanges). Charts without a tooltip formatter keep their tooltip as it
// is.
const withRangeTooltip = ({
  tooltip,
  formatValue,
}: {
  tooltip: Highcharts.TooltipOptions | undefined;
  formatValue: FormatValue;
}): Highcharts.TooltipOptions | undefined => {
  const format = tooltip && tooltip.formatter;
  if (!tooltip || !format) {
    return tooltip;
  }
  return {
    ...tooltip,
    formatter: function rangeFormatter(
      this: Highcharts.TooltipFormatterContextObject,
      chartTooltip: Highcharts.Tooltip,
    ) {
      const text = format.call(this, chartTooltip);
      return typeof text === 'string'
        ? withRanges({
            text,
            points: this.points ?? [],
            formatValue,
            isHtml: !!tooltip.useHTML,
          })
        : text;
    },
  };
};

// Adds the predicted buckets after each metric series of a time-based chart:
// on a datetime axis (Line) at their dates; on a category axis of dates (Bar,
// Combo) as more date categories. Other charts, comparisons (whose x values
// are indexes) and metrics without predictions are left alone.
export const withForecast = ({
  options,
  predicted,
  formatValue,
  showRange = true,
}: {
  options: Highcharts.Options;
  // The rows the chart's query predicted
  predicted: MetricsResponse;
  // The 95% range: a band around the line, and in the tooltip
  showRange?: boolean;
  // For each forecast point's range in the tooltip
  formatValue: FormatValue;
}): Highcharts.Options => {
  const axes = options.xAxis;
  const xAxis: Axis | undefined = Array.isArray(axes) ? axes[0] : axes;
  const series = (options.series ?? []) as SourceSeries[];
  const isDatetime = !!xAxis && xAxis.type === 'datetime';
  const categories =
    xAxis && Array.isArray(xAxis.categories) ? xAxis.categories : undefined;
  if (!xAxis || (!isDatetime && !categories) || predicted.length === 0) {
    return options;
  }

  const palette = options.colors ?? Highcharts.getOptions().colors ?? [];
  const colorAt = (index: number) =>
    String(palette[index % Math.max(palette.length, 1)] ?? '');

  // Predicted dates the category axis doesn't have yet, in order
  const futureCategories = categories
    ? Array.from(new Set(predicted.map((row) => String(row.date)))).filter(
        (date) => !categories.includes(date),
      )
    : [];
  const allCategories = categories ? [...categories, ...futureCategories] : [];

  const added: Highcharts.SeriesOptionsType[] = [];
  // Where the forecast period starts (the last actual point) and ends
  const lastXs: number[] = [];
  const forecastXs: number[] = [];
  const sources = series.map((s, index) => {
    if (!isSourceSeries(s) || !s.data || !s.metricId) {
      return s;
    }
    const forecast = toForecastPoints({ predicted, metricId: s.metricId });
    const data = s.data;
    const ys = data.map(toY);
    const lastIndex = ys.map(Number.isFinite).lastIndexOf(true);
    if (forecast.length === 0 || lastIndex === -1) {
      return s;
    }
    let lastX: number;
    let xs: number[];
    if (isDatetime) {
      const timestamps = data.map((p) =>
        Array.isArray(p) ? Number(p[0]) : NaN,
      );
      if (timestamps.some((t) => !Number.isFinite(t))) {
        return s;
      }
      lastX = timestamps[lastIndex];
      xs = forecast.map((p) => new Date(p.date).getTime());
    } else {
      lastX = lastIndex;
      xs = forecast.map((p) => allCategories.indexOf(p.date));
    }
    const id = s.id ?? `studio-forecast-source-${index}`;
    lastXs.push(lastX);
    forecastXs.push(...xs);
    added.push(
      ...forecastSeries({
        source: s,
        id,
        color: s.color ?? colorAt(index),
        xs,
        last: { x: lastX, y: ys[lastIndex] },
        forecast,
        showRange,
      }),
    );
    return { ...s, id };
  });
  if (added.length === 0) {
    return options;
  }

  // The forecast period shaded, from the last actual point to the last
  // predicted one (half a category further, to cover its bar)
  const start = Math.max(...lastXs);
  const end = Math.max(...forecastXs) + (categories ? 0.5 : 0);
  const nextXAxis: Axis = {
    ...xAxis,
    ...(categories ? { categories: allCategories } : {}),
    plotBands: [
      ...(xAxis.plotBands ?? []),
      {
        from: start,
        to: end,
        className: FORECAST_CLASS_NAMES.period,
      },
    ],
    plotLines: [
      ...(xAxis.plotLines ?? []),
      {
        value: start,
        className: FORECAST_CLASS_NAMES.start,
        dashStyle: 'Dash',
        width: 1,
      },
    ],
  };
  return {
    ...options,
    xAxis: Array.isArray(axes) ? [nextXAxis, ...axes.slice(1)] : nextXAxis,
    series: [...sources, ...added] as Highcharts.SeriesOptionsType[],
    tooltip: withRangeTooltip({ tooltip: options.tooltip, formatValue }),
  };
};
